import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import * as dayjs from "dayjs";
import EventRow from "../components/EventRow";
import Slideshow from "../components/Slideshow";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";

import client from "../components/client";
import ExitModal from "../components/ExitModal";
import FormMini from "../components/forms/FormMini";

export default function TemplatePage({ utm, events }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  let eventList = [];
  for (let event of events) {
    const now = dayjs();
    const eventDate =
      dayjs(event.event_date).format("MMM D, YYYY") != "Invalid Date" ? dayjs(event.event_date) : dayjs();

    if (event.event_type === "webinar" && eventList.length < 3 && eventDate > now) {
      eventList.push(event);
    }
  }
  if (eventList.length < 3) {
    for (let event of events) {
      if (event.event_type == "ondemand" && eventList.length < 3) {
        eventList.push(event);
      }
    }
  }

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>Developer-Friendly Tools for Building Exceptional Real Estate Experiences - Elm Street Technology</title>
        <meta
          name="description"
          content="Developers and potential partners receive real incentives, resources, technologies, and other services that integrate with our SaaS, small business and real estate solutions."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/agent-solutions" />
        <meta property="og:title" content="Partnership Program For Developers - Elm Street Technology" />
        <meta
          property="og:description"
          content="Developers and potential partners receive real incentives, resources, technologies, and other services that integrate with our SaaS, small business and real estate solutions."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      <div
        style={{
          backgroundImage: "url('dev2bg.jpg')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={6} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "650px",
                }}
              >
                <div>
                  <Typography variant="h2" component="h1">
                    Developer-Friendly Tools for Building Exceptional Real Estate Experiences
                  </Typography>
                  <p>Simplifying MLS integration for developers with powerful, flexible solutions.</p>
                  <Button variant="contained" color="secondary" href="#form">
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "30px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Built for Developers, Backed by Expertise</span>
                </Typography>
                <p>
                  As a developer, you need reliable, flexible tools to deliver exceptional real estate websites. Elm
                  Street Technology, through IDX Broker, offers industry-leading solutions that make it easy to
                  integrate MLS data, customize user experiences, and exceed your clients’ expectations.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/dev1.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street Technology Developer Partners - Built for Developers, Backed by Expertise"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "15px",
            paddingBottom: "45px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/dev2.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street Technology Developer Partners - A Partnership Designed for Success"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>A Partnership Designed for Success</span>
                </Typography>

                <p>
                  Partnering with IDX Broker gives you access to comprehensive developer resources. Our platform ensures
                  you have the tools to create high-performing, client-focused solutions. The Developer Program provides
                  a true partnership with incentives for designers and developers that integrate IDX Broker Lite or IDX
                  Broker Platinum into real estate websites.
                </p>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "15px",
          }}
        >
          <div style={{ marginTop: "25px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Flexible Solutions to Match Your Needs</span>
                </Typography>

                <p>
                  Every developer has unique requirements, and IDX Broker offers unmatched flexibility to meet them.
                  From advanced search and map integration to fully customizable design options, our solutions adapt to
                  your project’s scope and your client’s goals, providing a seamless integration experience.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  explore this &amp; more!
                </Button>
              </Grid>

              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/dev3.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street Technology Developer Partners - Flexible Solutions to Match Your Needs"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "35px",
            paddingBottom: "90px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/dev4.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street Technology Developer Partners - Dedicated Support and Collaboration"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Dedicated Support and Collaboration</span>{" "}
                </Typography>
                <p>
                  When you partner with Elm Street Technology, you’re not just getting access to cutting-edge
                  tools—you’re gaining a team of experts dedicated to your success. Our support team is here to guide
                  you through technical challenges, provide insights, and ensure your projects run smoothly from start
                  to finish.
                </p>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              Become a developer partner
            </Typography>
            <div style={{ paddingBottom: "40px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746" }}>
                Connect with Elm Street Technology's Success Team today to explore your path to partnership success.
              </strong>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successMsg={
                        <div style={{ color: "#d37800" }}>
                          <strong>
                            We're Excited to Connect with You! A member of the Elm Street Technology team will be in
                            touch with you shortly.
                          </strong>
                          <br />
                          <br />
                        </div>
                      }
                      listId="147997"
                      formName="es2_developerPartners"
                      fieldList={["first_name", "last_name", "email", "phone", "markets"]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>

      <MainFooter />
    </ThemeProvider>
  );
}

export async function getServerSideProps({ req, res }) {
  let cookies = getCookie("elmlead", { req, res });

  const eventQuery = `
  *[_type == "event" &&  !("Training" in event_category[]->title)] | order(event_date asc) {
    _id,
    title,
    event_date,
    event_type,
    event_category[]->{
      title
    },
    duration,
    description,
    thumbnail,
    reg_url,
    tags[]->{
      tag
    }
  }
  `;

  const events = await client.fetch(eventQuery);

  if (cookies) {
    return { props: { utm: cookies, events } };
  }
  return { props: { events } };
}
